<template>
  <div class="el-pagination is-background custom-pagination">
      <div class="el-card__body" style="width: 100%; padding: 0px;">
        <div class="el-pagination is-background">
          <span v-if="showTotal" key="total" class="el-pagination__total is-first">
            {{ innerTotalText }} {{ total }}
          </span>

          <!-- 每页显示数量选择 -->
          <span v-if="showSizes" class="el-pagination__sizes">
            <el-select v-model="innerPageSize" @change="handleSizeChange">
              <el-option v-for="size in pageSizes" :key="size" :value="size">{{ size }} {{innerPerPageText}}</el-option>
            </el-select>
          </span>

          <!-- 分页器主体 -->
            <button 
              class="btn-prev" 
              :disabled="innerCurrentPage <= 1"
              @click="handleCurrentChange(innerCurrentPage - 1)"
            >
              <i class="el-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="currentColor" d="M609.408 149.376 277.76 489.6a32 32 0 0 0 0 44.672l331.648 340.352a29.12 29.12 0 0 0 41.728 0 30.592 30.592 0 0 0 0-42.752L339.264 511.936l311.872-319.872a30.592 30.592 0 0 0 0-42.688 29.12 29.12 0 0 0-41.728 0z"></path></svg></i>
            </button>

            <!-- 页码列表 -->
            <ul class="el-pager">
              <template v-for="(page, index) in pager">
                <li v-if="page === '...'" class="more btn-quicknext el-icon" disabled >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="currentColor" d="M176 416a112 112 0 1 1 0 224 112 112 0 0 1 0-224m336 0a112 112 0 1 1 0 224 112 112 0 0 1 0-224m336 0a112 112 0 1 1 0 224 112 112 0 0 1 0-224"></path></svg>
                </li>
                <li v-else :key="index"
                  class="number"
                  :class="{ 'is-active': page === innerCurrentPage }"
                  @click="handleCurrentChange(page)"
                  >
                  {{ page }}
                </li>
              </template>
            </ul>

            <button
              class="btn-next" 
              :disabled="innerCurrentPage >= totalPages"
              @click="handleCurrentChange(innerCurrentPage + 1)"
            >
            <i class="el-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="currentColor" d="M340.864 149.312a30.592 30.592 0 0 0 0 42.752L652.736 512 340.864 831.872a30.592 30.592 0 0 0 0 42.752 29.12 29.12 0 0 0 41.728 0L714.24 534.336a32 32 0 0 0 0-44.672L382.592 149.376a29.12 29.12 0 0 0-41.728 0z"></path></svg></i>
            </button>

          <!-- 快速跳转 -->
          <div v-if="showJumper" class="el-pagination__jump is-last">
            <span class="el-pagination__goto">
              {{ innerGotoText }}
            </span>
            <div class="el-input el-pagination__editor is-in-pagination">
              <el-input
                type="number"
                :min="1"
                :max="totalPages"
                v-model.number="jumpPage"
                @keyup.enter="handleJump"
              />
            </div>
            <span class="el-pagination__classifier">
              {{ innerPageText }}
            </span>
          </div>
        </div>
      </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
const props = defineProps({
  total: { type: Number, required: true },         // 总数据量
  currentPage: { type: Number, default: 1 },       // 当前页码
  pageSize: { type: Number, default: 10 },         // 每页数量
  pageSizes: {                                     // 每页数量选项
    type: Array,
    default: () => [10, 20, 50, 100]
  },
  totalText: { type: String, default: '共' },
  itemText: { type: String, default: '条' },
  perPageText: { type: String, default: '条/页' },
  gotoText: { type: String, default: '前往' },
  pageText: { type: String, default: '页' },
  layout: { type: String, default: 'total, sizes,->, prev, pager, next, jumper' }
});



const emit = defineEmits([
  'update:currentPage',
  'update:pageSize',
  'current-change',
  'size-change'
]);

// 内部状态
const innerCurrentPage = ref(props.currentPage);
const innerPageSize = ref(props.pageSize);
const jumpPage = ref(props.currentPage);
const innerTotalText = ref(props.totalText);
const innerItemText = ref(props.itemText);
const innerPerPageText = ref(props.perPageText);
const innerGotoText = ref(props.gotoText);
const innerPageText = ref(props.pageText);

watch(() => props.totalText, (newVal) => {
  innerTotalText.value = newVal;
}, { immediate: true });

watch(() => props.itemText, (newVal) => {
  innerItemText.value = newVal;
}, { immediate: true });

watch(() => props.perPageText, (newVal) => {
  innerPerPageText.value = newVal;
}, { immediate: true });

watch(() => props.gotoText, (newVal) => {
  innerGotoText.value = newVal;
}, { immediate: true });

watch(() => props.pageText, (newVal) => {
  innerPageText.value = newVal;
}, { immediate: true });

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(props.total / innerPageSize.value);
});

// 显示逻辑判断
const showTotal = computed(() => props.layout.includes('total'));
const showSizes = computed(() => props.layout.includes('sizes'));
const showJumper = computed(() => props.layout.includes('jumper'));

// 生成页码数组
const pager = computed(() => {
  const current = innerCurrentPage.value;
  const pageCount = totalPages.value;
  const delta = 2; // 当前页前后显示页码数
  
  let range = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(pageCount - 1, current + delta); i++) {
    range.push(i);
  }

  if (current - delta > 2) range.unshift('...');
  if (current + delta < pageCount - 1) range.push('...');

  range.unshift(1);
  if (pageCount > 1) range.push(pageCount);

  return range.filter((x, i, arr) => arr.indexOf(x) === i);
});

// 处理页码变化
const handleCurrentChange = (page) => {
  page = Math.max(1, Math.min(page, totalPages.value));
  if (page !== innerCurrentPage.value) {
    innerCurrentPage.value = page;
    emit('update:currentPage', page);
    emit('current-change', page);
  }
};

// 处理每页数量变化
const handleSizeChange = () => {
  innerCurrentPage.value = 1;
  emit('update:pageSize', innerPageSize.value);
  emit('size-change', innerPageSize.value);
  emit('update:currentPage', 1);
};

// 处理快速跳转
const handleJump = () => {
  if (jumpPage.value && jumpPage.value >= 1 && jumpPage.value <= totalPages.value) {
    handleCurrentChange(Number(jumpPage.value));
    jumpPage.value = null;
  }
};

// 监听外部变化
watch(() => props.currentPage, (val) => {
  innerCurrentPage.value = val;
});

watch(() => props.pageSize, (val) => {
  innerPageSize.value = val;
});
</script>

<style scoped>
.custom-pagination {
  display: flex;
  gap: 12px;
}

.pagination-sizes select, .pagination-jumper input {
  margin: 0 6px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.pagination-body {
  display: flex;
  gap: 4px;
}
</style>